import { z } from "zod";

export const executeZslBodySchema = z.object({
  template: z.object({
    parameters: z.array(z.any()),
    name: z.string(),
    interfaces: z.array(z.any()).optional(),
    tables: z.array(z.any()).optional(),
    zaps: z.array(z.any()).optional(),
  }),
});
// Field option schema
const fieldOptions = z.object({
  indexed: z.boolean(),
  required: z.boolean(),
  read_only: z.boolean(),
  converted_from: z.null(),
  converted_from_version: z.null(),
  default: z.null(),
  static_choices: z
    .array(
      z.object({
        value: z.string(),
        label: z.string(),
        color: z.null(),
      }),
    )
    .nullable(),
  choice_provider: z
    .object({
      kind: z.string(),
      choices: z.array(
        z.object({
          value: z.string(),
          label: z.string(),
          color: z.null(),
        }),
      ),
    })
    .nullable(),
  formula: z.null(),
  button: z.null(),
  include_time: z.boolean(),
  timezone: z.string(),
  date_format: z.string(),
  time_format: z.string(),
  ai_formula: z.null(),
  icon: z.string(),
  alignment: z.string(),
  decimals: z.number(),
  currency_format: z.string(),
  phone_number_format: z.string(),
  description: z.string(),
  source_column_key: z.null(),
});

// Field schema
const field = z.object({
  id: z.number(),
  name: z.string(),
  type: z.enum([
    "string",
    "datetime",
    "labeled_string",
    "number",
    "text",
    "boolean",
    "email",
  ]),
  options: fieldOptions,
});

// Table schema
const table = z.object({
  id: z.string(),
  name: z.string(),
  fields: z.array(field),
});

// Resource map schema
const resourceMap = z.record(z.string(), z.string());

// Define the ZDL Step schema
const zdlStep = z.object({
  id: z.string(),
  app: z.string(),
  type: z.string(),
  action: z.string(),
  params: z.record(z.any(), z.any()),
  title: z.string(),
});

// Define the ZDL schema
const zdl = z.object({
  app: z.string(),
  type: z.string(),
  action: z.string(),
  id: z.string(),
  title: z.string(),
  zdl_version: z.string(),
  steps: z.array(zdlStep),
});

// Define the Zap schema
const zap = z.object({
  id: z.string(),
  title: z.string(),
  paused: z.boolean(),
  zdl: zdl,
});

// Main schema
export const executeZslResponseSchema = z.object({
  interfaces: z.record(
    z.string(),
    z.object({
      id: z.string(),
      name: z.string(),
      slug: z.string(),
      customDomain: z.null(),
      homepageId: z.null(),
      appearance: z.null(),
      hideZapierButton: z.boolean(),
      googleAnalyticsTrackingId: z.null(),
      logo: z.null(),
      favicon: z.object({
        name: z.string(),
        url: z.string(),
      }),
      creatorId: z.string(),
      accountId: z.number(),
      globalNavigation: z.boolean(),
      includeLogo: z.boolean(),
      navigationLinks: z.array(z.any()),
      authType: z.string(),
      createdAt: z.string(),
      updatedAt: z.string(),
      pages: z.record(
        z.string(),
        z.object({
          id: z.string(),
          name: z.string(),
          title: z.string(),
          slug: z.string(),
          content: z.object({
            version: z.string(),
            blocks: z.array(
              z.object({
                type: z.string(),
                config: z
                  .object({
                    content: z.string().optional(),
                    textAlign: z.string().optional(),
                    id: z.string(),
                    submitText: z.string().optional(),
                    triggers: z.array(z.any()).optional(),
                    storageType: z.string().optional(),
                    storageId: z.string().optional(),
                    captchaEnabled: z.boolean().optional(),
                  })
                  .and(z.record(z.any(), z.any())),
                children: z.array(z.any()).optional(),
              }),
            ),
          }),
          projectId: z.string(),
          createdAt: z.string(),
          updatedAt: z.string(),
          navigationEnabled: z.boolean(),
        }),
      ),
    }),
  ),
  creator: z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.email(),
    avatarUrl: z.url(),
    isStaff: z.boolean(),
  }),
  canvases: z.record(z.string(), z.unknown()),
  tables: z.record(z.string(), table),
  chatbots: z.record(z.string(), z.unknown()),
  zaps: z.record(z.string(), zap),
  resourceMap: resourceMap,
});

// Type inference
export type ExecuteZslResponse = z.infer<typeof executeZslResponseSchema>;
export type ExecuteZslBody = z.infer<typeof executeZslBodySchema>;
export async function executeZsl(
  body: z.infer<typeof executeZslBodySchema>,
): Promise<z.infer<typeof executeZslResponseSchema>> {
  const response = await fetch("/api/canvasui/zsl/execute", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(
      error.message ||
        `Whoops! We can't build that suggestion right now. Please edit the prompt and try again.`,
    );
  }

  const { data } = await response.json();
  //todo: jake - figure out more "live" or relaxed or logged validation
  // const safeData =  executeZslResponseSchema.safeParse(data);
  // if (!safeData.success) {
  //     console.error(`Failed to parse response: ${safeData.error.message}`);
  //     throw new Error('Failed to parse response');
  // }
  // return safeData.data;
  return data;
}
